import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import Select from 'react-select'
// import { authService } from '../services/authService'
import { registerService } from '../services/registerService'
import { useBankIcons } from '../contexts/BankContext'
import type { TenantThemeConfig } from '../types/theme'
import { getThemeAssetUrl } from '../utils/themeUtils'

interface RegisterCompletePageProps {
  themeConfig: TenantThemeConfig
  assetConfig: {
    CDN_BASE_URL: string
    CSS_PATH: string
    IMG_PATH: string
    FONTS_PATH: string
  }
}

interface LocationState {
  phoneNumber: string
  sessionId: string
  smsVerified: boolean
  provider?: string
  firebaseUid?: string
  method?: string
}

interface BankOption {
  value: string
  label: string
  imageUrl: string
}

const RegisterCompletePage: React.FC<RegisterCompletePageProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState
  const { getAvailableBanks, isLoadingBankIcons } = useBankIcons()

  // Get theme-specific assets path
  const getAssetUrl = (path: string) => {
    return getThemeAssetUrl(assetConfig.CDN_BASE_URL, themeConfig.themeId, path)
  }

  // Extract configuration from themeConfig
  const { branding } = themeConfig.theme

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    bankCode: '',
    accountNumber: '',
    bankImageUrl: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationErrors, setValidationErrors] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    bankCode: '',
    accountNumber: ''
  })
  const [isCheckingUsername, setIsCheckingUsername] = useState(false)
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [mobileTaken, setMobileTaken] = useState(false)
  const [bankAccountTaken, setBankAccountTaken] = useState(false)
  const [bankAccountCheckMessage, setBankAccountCheckMessage] = useState('')
  const [isCheckingBankAccount, setIsCheckingBankAccount] = useState(false)

  // Derived: รหัสผ่านยืนยันตรงกับรหัสผ่านหรือไม่ (สำหรับแสดงไอคอนติ๊กถูกและข้อความสำเร็จ)
  const confirmPasswordMatch =
    !!formData.password &&
    !!formData.confirmPassword &&
    formData.password === formData.confirmPassword

  // ห้ามเข้าหน้านี้ก่อน confirm OTP — ต้องมี state จากหน้า register หลังยืนยัน OTP (phoneNumber, sessionId, smsVerified)
  useEffect(() => {
    if (!state?.phoneNumber || !state?.sessionId || !state?.smsVerified) {
      navigate('/register', { replace: true })
      return
    }
  }, [state?.phoneNumber, state?.sessionId, state?.smsVerified, navigate])


  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear validation error when user starts typing
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }

    // Real-time validation for specific fields
    if (name === 'confirmPassword') {
      validateConfirmPassword(value)
    } else if (name === 'password') {
      validateConfirmPassword(formData.confirmPassword, value)
    } else if (name === 'fullName') {
      validateFullName(value)
    } else if (name === 'accountNumber') {
      validateAccountNumber(value)
      if (validationErrors.bankCode) setValidationErrors(prev => ({ ...prev, bankCode: '' }))
    }
  }

  const handleBankChange = (selectedOption: BankOption | null) => {
    if (selectedOption) {
      setFormData(prev => ({ 
        ...prev, 
        bankCode: selectedOption.value,
        bankImageUrl: selectedOption.imageUrl
      }))
      
      // Clear validation error
      if (validationErrors.bankCode) {
        setValidationErrors(prev => ({ ...prev, bankCode: '' }))
      }
    } else {
      setFormData(prev => ({ 
        ...prev, 
        bankCode: '',
        bankImageUrl: ''
      }))
    }
  }

  // Validate confirm password in real-time (passwordOverride ใช้เมื่อผู้ใช้แก้ไขฟิลด์รหัสผ่าน)
  const validateConfirmPassword = (confirmPassword: string, passwordOverride?: string) => {
    const password = passwordOverride ?? formData.password
    if (confirmPassword && password && confirmPassword !== password) {
      setValidationErrors(prev => ({
        ...prev,
        confirmPassword: 'รหัสผ่านไม่ตรงกัน'
      }))
    } else {
      setValidationErrors(prev => ({
        ...prev,
        confirmPassword: ''
      }))
    }
  }

  // Validate full name in real-time
  const validateFullName = (fullName: string) => {
    if (fullName && fullName.trim() === '') {
      setValidationErrors(prev => ({
        ...prev,
        fullName: 'กรุณากรอกชื่อ-นามสกุล'
      }))
    } else {
      setValidationErrors(prev => ({
        ...prev,
        fullName: ''
      }))
    }
  }

  // Validate account number in real-time
  const validateAccountNumber = (accountNumber: string) => {
    if (accountNumber && accountNumber.trim() === '') {
      setValidationErrors(prev => ({
        ...prev,
        accountNumber: 'กรุณากรอกหมายเลขบัญชี'
      }))
    } else if (accountNumber && !/^\d+$/.test(accountNumber.trim())) {
      setValidationErrors(prev => ({
        ...prev,
        accountNumber: 'หมายเลขบัญชีต้องเป็นตัวเลขเท่านั้น'
      }))
    } else {
      setValidationErrors(prev => ({
        ...prev,
        accountNumber: ''
      }))
    }
  }

  // Check username availability
  const checkUsernameAvailability = async (username: string) => {
    if (!username || username.length < 6) {
      setUsernameAvailable(null)
      return
    }

    if (!/^[a-zA-Z0-9]+$/.test(username)) {
      setUsernameAvailable(false)
      return
    }

    setIsCheckingUsername(true)
    try {
      const response = await registerService.validateUsernameAPI(username)
      setUsernameAvailable(response.available)
      setUsernameSuggestions(response.suggestions || [])
      setShowSuggestions(!response.available && (response.suggestions || []).length > 0)
    } catch (error) {
      console.error('❌ Username validation error:', error)
      setUsernameAvailable(false)
      setUsernameSuggestions([])
      setShowSuggestions(false)
    } finally {
      setIsCheckingUsername(false)
    }
  }

  // Debounced username check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.username) {
        checkUsernameAvailability(formData.username)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timeoutId)
  }, [formData.username])

  // เช็คเบอร์โทรว่าสมัครแล้วหรือยัง (ซ้ำในระบบ = disable ปุ่ม)
  useEffect(() => {
    if (!state?.phoneNumber) return
    registerService.checkMobileAvailability(state.phoneNumber).then((r) => {
      setMobileTaken(r.success && !r.available)
    })
  }, [state?.phoneNumber])

  // เช็คคู่ธนาคาร+หมายเลขบัญชีว่าถูกใช้แล้วหรือยัง (debounce)
  useEffect(() => {
    const code = (formData.bankCode || '').trim()
    const num = (formData.accountNumber || '').replace(/\D/g, '')
    if (!code || num.length < 10 || num.length > 12) {
      setBankAccountTaken(false)
      setBankAccountCheckMessage('')
      setValidationErrors(prev => ({ ...prev, bankCode: '' }))
      return
    }
    let cancelled = false
    setIsCheckingBankAccount(true)
    const t = setTimeout(() => {
      registerService.checkBankAccountAvailability(code, num).then((r) => {
        if (!cancelled) {
          const taken = r.success && !r.available
          setBankAccountTaken(taken)
          const msg = taken ? (r.message || 'เลขบัญชีนี้มีผู้ใช้งานแล้วในระบบ') : ''
          setBankAccountCheckMessage(msg)
          if (taken) setValidationErrors(prev => ({ ...prev, bankCode: msg }))
          else setValidationErrors(prev => ({ ...prev, bankCode: '' }))
        }
        if (!cancelled) setIsCheckingBankAccount(false)
      })
    }, 500)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [formData.bankCode, formData.accountNumber])

  // Handle username suggestion selection
  const handleSuggestionClick = (suggestion: string) => {
    setFormData(prev => ({
      ...prev,
      username: suggestion
    }))
    setShowSuggestions(false)
    setUsernameSuggestions([])
  }

  // Validate form
  const validateForm = (): string | null => {
    if (mobileTaken) {
      return 'เบอร์โทรศัพท์นี้มีผู้ใช้งานแล้วในระบบ'
    }

    if (!formData.username.trim()) {
      return 'กรุณากรอกยูสเซอร์'
    }
    
    if (formData.username.length < 6) {
      return 'ยูสเซอร์ต้องมีอย่างน้อย 6 อักษร'
    }
    
    if (!/^[a-zA-Z0-9]+$/.test(formData.username)) {
      return 'ยูสเซอร์ใช้ได้เฉพาะภาษาอังกฤษและตัวเลขเท่านั้น'
    }

    if (usernameAvailable === false) {
      return 'ยูสเซอร์นี้มีผู้ใช้งานแล้ว'
    }

    if (isCheckingUsername) {
      return 'กำลังตรวจสอบยูสเซอร์...'
    }

    if (bankAccountTaken) {
      return bankAccountCheckMessage || 'เลขบัญชีนี้มีผู้ใช้งานแล้วในระบบ'
    }

    if (isCheckingBankAccount) {
      return 'กำลังตรวจสอบบัญชีธนาคาร...'
    }

    if (!formData.password.trim()) {
      return 'กรุณากรอกรหัสผ่าน'
    }
    
    if (formData.password.length < 6) {
      return 'รหัสผ่านต้องมีอย่างน้อย 6 อักษร'
    }

    if (formData.password !== formData.confirmPassword) {
      return 'รหัสผ่านไม่ตรงกัน'
    }

    if (!formData.fullName || formData.fullName.trim() === '') {
      return 'กรุณากรอกชื่อ-นามสกุล'
    }

    if (!formData.bankCode || formData.bankCode.trim() === '') {
      return 'กรุณาเลือกธนาคาร'
    }

    if (!formData.accountNumber || formData.accountNumber.trim() === '') {
      return 'กรุณากรอกหมายเลขบัญชี'
    }

    if (!/^\d+$/.test(formData.accountNumber.trim())) {
      return 'หมายเลขบัญชีต้องเป็นตัวเลขเท่านั้น'
    }

    return null
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const validationError = validateForm()
    if (validationError) {
      await Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: validationError,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }

    setIsSubmitting(true)

    try {
      console.log('📝 Completing user registration:', {
        phoneNumber: state.phoneNumber,
        username: formData.username,
        provider: state.provider,
        method: state.method,
        smsVerified: state.smsVerified
      })

      // Referral code from affiliate link (?aff=) stored on RegisterPage
      const referralCode = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('affiliate_referral_code') : null

      // Call user registration API
      const response = await registerService.completeRegistration(
        state.phoneNumber,
        formData.username,
        formData.password,
        formData.fullName,
        formData.bankCode,
        formData.accountNumber,
        state.sessionId,
        state.smsVerified || false,
        state.method || 'unknown',
        formData.bankImageUrl,
        referralCode ?? undefined
      )

      if (response.success && referralCode) {
        try { sessionStorage.removeItem('affiliate_referral_code') } catch { /* ignore */ }
      }

      if (response.success) {
        console.log('✅ Registration completed successfully:', response.data)
        
        await Swal.fire({
          icon: 'success',
          title: 'สมัครสมาชิกสำเร็จ',
          text: `ยินดีต้อนรับสู่ระบบ ${response.data.fullName}! กรุณาเข้าสู่ระบบเพื่อเริ่มใช้งาน`,
          confirmButtonText: 'เข้าสู่ระบบ',
          confirmButtonColor: '#28a745',
          timer: 3000,
          timerProgressBar: true
        })

        // Navigate to login page
        navigate('/login', { replace: true })
      } else {
        // API returned success: false, extract the error message
        const errorMessage = response.message || 'ไม่สามารถสมัครสมาชิกได้'
        const errorTitle = 'ไม่สามารถสมัครสมาชิกได้'
        
        await Swal.fire({
          icon: 'error',
          title: errorTitle,
          text: errorMessage,
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#dc3545'
        })
        return
      }

    } catch (error) {
      console.error('❌ Registration completion error:', error)
      
      // Extract error message from API response
      let errorMessage = 'ไม่สามารถสมัครสมาชิกได้ กรุณาลองใหม่อีกครั้ง'
      let errorTitle = 'เกิดข้อผิดพลาด'
      
      if (error instanceof Error && error.message) {
        errorMessage = error.message
        errorTitle = 'ไม่สามารถสมัครสมาชิกได้'
      }
      
      await Swal.fire({
        icon: 'error',
        title: errorTitle,
        text: errorMessage,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle back button
  const handleBack = () => {
    navigate(-1)
  }

  
  // Get available banks from tenant configuration (only active banks)
  const bankOptions: BankOption[] = getAvailableBanks()
    .filter(bank => bank.isActive !== false) // Only active banks
    .map(bank => ({
      value: bank.abbreviation,
      label: bank.name,
      imageUrl: bank.imageUrl
    }))

  // console.log("🏦 BankOptions in RegisterCompletePage:", bankOptions);

  // Custom components for react-select
  const CustomOption = (props: any) => {
    const { data, innerProps, innerRef, isSelected, isFocused } = props;
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          ...innerProps.style,
          display: 'flex',
          alignItems: 'center',
          padding: '10px 15px',
          cursor: 'pointer',
          backgroundColor: isSelected ? '#e3f2fd' : isFocused ? '#f5f5f5' : 'white',
          borderBottom: '1px solid #eee'
        }}
      >
        {data.imageUrl && (
          <img 
            src={data.imageUrl} 
            alt={data.label}
            style={{
              width: '24px',
              height: '24px',
              marginRight: '12px',
              objectFit: 'contain',
              borderRadius: '4px'
            }}
          />
        )}
        <span style={{ 
          fontSize: '14px',
          color: '#333',
          fontWeight: isSelected ? '500' : '400'
        }}>
          {data.label}
        </span>
      </div>
    );
  };

  const CustomSingleValue = (props: any) => {
    const { data } = props;
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        fontSize: '14px',
        color: '#333',
        height: '100%',
        lineHeight: '1'
      }}>
        {data.imageUrl && (
          <img 
            src={data.imageUrl} 
            alt={data.label}
            style={{
              width: '20px',
              height: '20px',
              marginRight: '8px',
              objectFit: 'contain',
              borderRadius: '3px',
              verticalAlign: 'middle',
              display: 'inline-block'
            }}
          />
        )}
        <span style={{
          verticalAlign: 'middle',
          display: 'inline-block',
          lineHeight: '20px'
        }}>
          {data.label}
        </span>
      </div>
    );
  };

  const toLocalPhoneDisplay = (phoneNumber?: string) => {
    if (!phoneNumber) return ''
    if (phoneNumber.startsWith('+66')) {
      return `0${phoneNumber.slice(3)}`
    }
    return phoneNumber
  }

  const maskLocalPhone = (phoneNumber?: string) => {
    const localPhone = toLocalPhoneDisplay(phoneNumber)
    if (!localPhone) return ''
    const digits = localPhone.replace(/\D/g, '')
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}***${digits.slice(-4)}`
    }
    return localPhone
  }

  // Mask phone number for display
  const maskedPhone = maskLocalPhone(state?.phoneNumber)
  const displayPhoneNumber = toLocalPhoneDisplay(state?.phoneNumber)

  return (
    <>
      <style>{`
        .bank-select {
          width: 100% !important;
        }
        .bank-select .react-select__control {
          width: 100% !important;
        }
        .bank-select .react-select__value-container {
          width: 100% !important;
        }
      `}</style>
      <div className="bg-fixed"></div>
      <div className="content py-3">
      <div className="container">
        <div className="row mx--1">
          <div className="col-12 col-lg-6 offset-lg-3">
            <div className="box-login text-center bgi-2 bdr-10 c-white bx-sd p-2 mb-3">
              <div className="bgc-16 bdr-10 p-3 p-lg-4">
                <div className="box-regis w-100">
                  <div className="img-regis mx-auto">
                    <img src={getAssetUrl('/img/logo-m.png')} alt={branding.siteName} />
                  </div>
                </div>
                <h3 className="txt-user mb-1 text-center t-medium">สมัครสมาชิก</h3>

                <div className="wrap justify-content-center mb-3">
                  <small className="bgg-2 bdr-10 c-dark px-2 t-regular me-1">
                    STEP 3
                  </small>
                  <h6 className="text-center">สร้างบัญชี ({maskedPhone})</h6>
                </div>

                <div className="box-login px-4">
                  <form onSubmit={handleSubmit}>
                    {/* เบอร์โทรศัพท์ (ยืนยันแล้วจาก OTP) — ใช้ล็อกอินได้ทั้งเบอร์หรือ username */}
                    <div className="form-inp bdr-10 position-relative">
                      <div className="icon ps-1">
                        <i className="fas fa-mobile-alt"></i>
                      </div>
                      <input
                        type="text"
                        value={displayPhoneNumber}
                        readOnly
                        placeholder="เบอร์โทรศัพท์"
                        className="bg-light"
                        aria-label="เบอร์โทรศัพท์ที่ยืนยันแล้ว"
                      />
                    </div>
                    <small className={mobileTaken ? 'text-danger d-block' : 'text-muted d-block'}>
                      {mobileTaken ? 'เบอร์โทรศัพท์นี้มีผู้ใช้งานแล้วในระบบ' : 'เบอร์ที่ยืนยัน OTP แล้ว (ใช้ล็อกอินได้ทั้งเบอร์หรือยูสเซอร์)'}
                    </small>

                    {/* Username */}
                    <div className="form-inp bdr-10 mt-3 position-relative">
                      <div className="icon ps-1">
                        <i className="fas fa-user"></i>
                      </div>
                      <input 
                        type="text" 
                        name="username"
                        placeholder="ยูสเซอร์ผู้ใช้ (ไอดี):"
                        value={formData.username}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={validationErrors.username ? 'is-invalid' : ''}
                      />
                      {isCheckingUsername && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      )}
                      {usernameAvailable === true && !isCheckingUsername && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                          <i className="fas fa-check text-success"></i>
                        </div>
                      )}
                      {usernameAvailable === false && !isCheckingUsername && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                          <i className="fas fa-times text-danger"></i>
                        </div>
                      )}
                    </div>
                    <small className={validationErrors.username ? 'text-danger' : ''}>
                      {validationErrors.username || 'ยูสเซอร์ (ขั้นต่ำ 6 อักษร ภาษาอังกฤษ/ตัวเลขเท่านั้น)'}
                    </small>

                    {/* Username Suggestions */}
                    {showSuggestions && usernameSuggestions.length > 0 && (
                      <div className="mt-2">
                        <small className="text-muted d-block mb-2">
                          💡 ชื่อผู้ใช้ที่แนะนำ:
                        </small>
                        <div className="d-flex flex-wrap gap-2">
                          {usernameSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleSuggestionClick(suggestion)}
                              style={{ fontSize: '0.75rem' }}
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Password */}
                    <div className="form-inp bdr-10 mt-3">
                      <div className="icon ps-1">
                        <i className="fas fa-key"></i>
                      </div>
                      <input 
                        type="password" 
                        name="password"
                        placeholder="รหัสผ่าน:"
                        value={formData.password}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                      />
                    </div>
                    <small className="">
                      ขั้นต่ำ 6 ตัวอักษร , ตัวอักษร เล็กใหญ่ ตัวเลข อักขระ
                    </small>

                    {/* Confirm Password */}
                    <div className="form-inp bdr-10 mt-3 position-relative">
                      <div className="icon ps-1">
                        <i className="fas fa-key"></i>
                      </div>
                      <input 
                        type="password" 
                        name="confirmPassword"
                        placeholder="ยืนยันรหัสผ่าน:"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={validationErrors.confirmPassword ? 'is-invalid' : confirmPasswordMatch ? 'is-valid' : ''}
                      />
                      {confirmPasswordMatch && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                          <i className="fas fa-check text-success"></i>
                        </div>
                      )}
                    </div>
                    <small className={validationErrors.confirmPassword ? 'text-danger' : confirmPasswordMatch ? 'text-success' : ''}>
                      {validationErrors.confirmPassword
                        ? validationErrors.confirmPassword
                        : confirmPasswordMatch
                          ? <>
                              <i className="fas fa-check-circle me-1" aria-hidden />
                              ยืนยันความถูกต้องของรหัสผ่านแล้ว
                            </>
                          : 'กรุณาใส่รหัสผ่านยืนยันอีกครั้งให้เหมือนกัน'}
                    </small>
                  </form>

                  {/* Bank Account Section */}
                  <div className="box-regis mt-4" style={{ width: '100%' }}>
                    <h4 className="my-1 text-center t-medium">ข้อมูลบัญชีธนาคาร</h4>
                  </div>

                  <form>
                    {/* Full Name */}
                    <div className="form-inp bdr-10">
                      <div className="icon ps-1">
                        <i className="fas fa-user-plus"></i>
                      </div>
                      <input 
                        type="text" 
                        name="fullName"
                        placeholder="ชื่อ-นามสกุล"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={validationErrors.fullName ? 'is-invalid' : ''}
                      />
                    </div>
                    <small className={validationErrors.fullName ? 'text-danger' : ''}>
                      {validationErrors.fullName || 'กรอกให้ตรงกับชื่อบัญชีของธนาคาร (ข้อมูลนี้ไม่สามารถแก้ไขได้)'}
                    </small>

                    {/* Bank Selection */}
                    <div className="form-inp bdr-10 mt-3" style={{ width: '100%' }}>
                      <div className="icon ps-1">
                        <i className="fas fa-university"></i>
                      </div>
                      <Select
                        classNamePrefix="react-select"
                        options={bankOptions}
                        value={bankOptions.find(option => option.value === formData.bankCode) || null}
                        onChange={handleBankChange}
                        isDisabled={isSubmitting || isLoadingBankIcons}
                        placeholder={isLoadingBankIcons ? 'กำลังโหลดธนาคาร...' : 'เลือกธนาคาร'}
                        components={{
                          Option: CustomOption,
                          SingleValue: CustomSingleValue
                        }}
                        styles={{
                          container: (base) => ({
                            ...base,
                            width: '100%'
                          }),
                          control: (provided, state) => ({
                            ...provided,
                            border: validationErrors.bankCode ? '1px solid #dc3545' : '1px solid #ced4da',
                            borderRadius: '8px',
                            minHeight: '45px',
                            backgroundColor: 'white',
                            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
                            width: '100%',
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover': {
                              border: validationErrors.bankCode ? '1px solid #dc3545' : '1px solid #ced4da'
                            }
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#6c757d',
                            fontSize: '14px'
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            border: '1px solid #e0e0e0',
                            marginTop: '4px',
                            minWidth: '300px',
                            width: 'auto'
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            padding: '4px 0',
                            maxHeight: '200px'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected ? '#e3f2fd' : state.isFocused ? '#f5f5f5' : 'white',
                            color: '#333',
                            fontSize: '14px',
                            padding: '10px 15px',
                            '&:hover': {
                              backgroundColor: '#f5f5f5'
                            }
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#333',
                            fontSize: '14px'
                          }),
                          indicatorSeparator: () => ({
                            display: 'none'
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#6c757d',
                            '&:hover': {
                              color: '#333'
                            }
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '2px 8px',
                            height: '100%'
                          })
                        }}
                        // className={validationErrors.bankCode ? 'is-invalid' : ''}
                      />
                    </div>
                    <small className={validationErrors.bankCode || bankAccountTaken ? 'text-danger' : ''}>
                      {validationErrors.bankCode || (bankAccountTaken ? bankAccountCheckMessage : '') || 'บัญชีธนาคารเพื่อใช้ถอนเมื่อถูกรางวัล'}
                    </small>
                    {isCheckingBankAccount && (
                      <small className="text-muted d-block">กำลังตรวจสอบบัญชีธนาคาร...</small>
                    )}

                    {/* Account Number */}
                    <div className="form-inp bdr-10 mt-3">
                      <div className="icon ps-1">
                        <i className="fas fa-credit-card"></i>
                      </div>
                      <input 
                        type="text" 
                        name="accountNumber"
                        placeholder="หมายเลขบัญชี"
                        value={formData.accountNumber}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={validationErrors.accountNumber ? 'is-invalid' : ''}
                      />
                    </div>
                    <small className={validationErrors.accountNumber ? 'text-danger' : ''}>
                      {validationErrors.accountNumber || 'หมายเลขบัญชี (ตัวเลขเท่านั้น)'}
                    </small>
                  </form>

                  {/* Action Buttons */}
                  <div className="row mx--1 mt-4">
                    <div className="col px-1">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="btn_main c-black wrap justify-content-center t-medium bgc-17 bdr-10"
                        disabled={isSubmitting}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        กลับ
                      </button>
                    </div>
                    <div className="col px-1">
                      <button
                        type="submit"
                        onClick={handleSubmit}
                        className="btn_main c-black wrap justify-content-center t-medium bgg-2 c-red bdr-10"
                        disabled={
                          isSubmitting ||
                          mobileTaken ||
                          usernameAvailable === false ||
                          bankAccountTaken ||
                          isCheckingBankAccount
                        }
                      >
                        {isSubmitting ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            กำลังสมัคร...
                          </>
                        ) : mobileTaken ? (
                          <>เบอร์โทรนี้มีผู้ใช้แล้ว</>
                        ) : bankAccountTaken ? (
                          <>เลขบัญชีนี้มีผู้ใช้แล้ว</>
                        ) : usernameAvailable === false ? (
                          <>ยูสเซอร์นี้มีผู้ใช้แล้ว</>
                        ) : (
                          <>ถัดไป →</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Footer */}
      <footer className="footer c-red text-center py-2 mb-4 animateMe" data-animation="fadeInUp">
        <small>Copyright © 2023-2024 All rights reserved.</small>
      </footer>
    </>
  )
}

export default RegisterCompletePage
